import { useCallback } from 'react';
import { SEND_FORM_STEPS } from '../constants';
import { useSendFormsContext, useSendFormsStore } from '../providers';
import { SendFormStep, SendFormsOrigin } from '../types';

interface UseStepsResults {
  goToNextStep: () => void;
  goBackAStep: () => void;
  resetSteps: () => void;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;
  goToStep: (step: SendFormStep) => void;
}

export const useSteps = (): UseStepsResults => {
  const { origin } = useSendFormsContext();
  const { setCurrentStep, getStepData } = useSendFormsStore(['setCurrentStep', 'getStepData']);

  const goToNextStep = useCallback(() => {
    const { steps, currentStep } = getStepData();
    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex === steps.length - 1) {
      return;
    }

    setCurrentStep(steps[currentIndex + 1]);
  }, [getStepData, setCurrentStep]);

  const goBackAStep = useCallback(() => {
    const { steps, currentStep } = getStepData();
    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex === 0) {
      return;
    }

    setCurrentStep(steps[currentIndex - 1]);
  }, [getStepData, setCurrentStep]);

  const goToStep = useCallback(
    (step: SendFormStep) => {
      setCurrentStep(step);
    },
    [setCurrentStep]
  );

  const resetSteps = useCallback(() => {
    const firstStep =
      origin === SendFormsOrigin.FORMS ? SEND_FORM_STEPS.formsOrigin[0] : SEND_FORM_STEPS.contactsOrigin[0];
    setCurrentStep(firstStep);
  }, [origin, setCurrentStep]);

  const isLastStep = useCallback(() => {
    const { currentStep, steps } = getStepData();
    return currentStep === steps[steps.length - 1];
  }, [getStepData]);

  const isFirstStep = useCallback(() => {
    const { currentStep, steps } = getStepData();
    return currentStep === steps[0];
  }, [getStepData]);

  return {
    goToNextStep,
    goBackAStep,
    goToStep,
    resetSteps,
    isFirstStep,
    isLastStep,
  };
};
